// exportExcel.js
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import * as services from "../../../services/saleng";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sumItems = (items, fields) =>
  (items || []).reduce((sum, item) => {
    const field = fields.find((name) => item?.[name] != null);
    return sum + toNumber(field ? item[field] : 0);
  }, 0);

const pickValue = (source, fields) =>
  fields.map((field) => source?.[field]).find((value) => value);

const getBranchDetails = async (transaction) => {
  const shopFromTransaction = transaction?.shop;
  const branchFromTransaction = transaction?.branch;

  try {
    const response = await services.getShop(transaction?.shopId);
    const shop = response?.data?.data;
    const branchId = transaction?.branchId || branchFromTransaction?.id;
    const branch =
      (shop?.branches || []).find((item) => item?.id === branchId) ||
      branchFromTransaction;

    return { shop: shop || shopFromTransaction, branch };
  } catch (error) {
    console.warn("Unable to load branch details for receiving form:", error);
    return { shop: shopFromTransaction, branch: branchFromTransaction };
  }
};

const getAddressText = (branch, shop) => {
  const address =
    pickValue(branch, [
      "address",
      "addressText",
      "fullAddress",
      "location",
      "description",
    ]) ||
    pickValue(shop, ["address", "addressText", "fullAddress", "location"]);

  const parts = [
    address,
    pickValue(branch, ["subDistrict", "subdistrict", "tambon"]),
    pickValue(branch, ["district", "amphoe"]),
    pickValue(branch, ["province"]),
    pickValue(branch, ["postcode", "zipCode", "zipcode"]),
  ].filter(Boolean);

  return [...new Set(parts)].join(" ");
};

const getImageUrl = (branch, shop) =>
  pickValue(branch, [
    "imageUrl",
    "image",
    "logoUrl",
    "logo",
    "pictureUrl",
    "photoUrl",
  ]) ||
  pickValue(shop, [
    "imageUrl",
    "image",
    "logoUrl",
    "logo",
    "pictureUrl",
    "photoUrl",
  ]);

const getImageExtension = (url, contentType) => {
  if (contentType?.includes("png") || url?.includes(".png")) return "png";
  return "jpeg";
};

const addBranchImage = async (workbook, worksheet, imageUrl) => {
  if (!imageUrl) return;

  try {
    if (imageUrl.startsWith("data:image/")) {
      const [meta, base64] = imageUrl.split(",");
      const extension = meta.includes("png") ? "png" : "jpeg";
      const imageId = workbook.addImage({ base64, extension });
      worksheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 131, height: 131 } });
      return;
    }

    const response = await fetch(imageUrl);
    if (!response.ok) return;

    const arrayBuffer = await response.arrayBuffer();
    const extension = getImageExtension(
      imageUrl,
      response.headers.get("content-type"),
    );
    const imageId = workbook.addImage({ buffer: arrayBuffer, extension });
    worksheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 131, height: 131 } });
  } catch (error) {
    console.warn("Unable to load branch image for receiving form:", error);
  }
};

export async function handleExportExcel(selectedData) {
  if (!selectedData || Object.keys(selectedData).length === 0) {
    alert("ไม่พบข้อมูลที่ตรงกับที่เลือก");
    return;
  }

  const response = await fetch("/DocumentsReceiving_new.xlsx");
  const arrayBuffer = await response.arrayBuffer();

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  const worksheet = workbook.worksheets[0];
  const { shop, branch } = await getBranchDetails(selectedData);

  const headerRowNumber = 6;
  const headerRow = worksheet.getRow(headerRowNumber);

  const headerMap = {};
  const headerEntries = [];
  headerRow.eachCell((cell, colNumber) => {
    if (cell.value) {
      const header = cell.value.toString().trim();
      headerMap[header] = colNumber;
      headerEntries.push([header, colNumber]);
    }
  });

  const findHeaderColumn = (...parts) => {
    const entry = headerEntries.find(([header]) =>
      parts.every((part) => header.includes(part)),
    );
    return entry?.[1];
  };

  let startRow = headerRowNumber + 1;
  while (worksheet.getRow(startRow).getCell(1).value) {
    worksheet.spliceRows(startRow, 1);
  }

  const totalWeight =
    toNumber(selectedData.totalWeight) ||
    sumItems(selectedData.items, ["netWeight", "weight"]);

  const totalAmount =
    toNumber(selectedData.totalAmount) ||
    sumItems(selectedData.items, ["netPrice", "totalPrice"]);

  const row = worksheet.getRow(startRow);

  const noColumn = findHeaderColumn("No./") || headerMap["No./ลำดับ"];
  const farmerColumn =
    findHeaderColumn("ID Famer") || headerMap["รหัสเกษตรกร / ID Famer"];
  const weightColumn =
    findHeaderColumn("จำนวนรับเข้า") || headerMap["จำนวนรับเข้า/กก."];
  const amountColumn = findHeaderColumn("ยอดรวม") || headerMap["ยอดรวม"];

  row.getCell(noColumn).value = 1;
  row.getCell(farmerColumn).value =
    selectedData.customer?.name || selectedData.customerName || "";

  row.getCell(weightColumn).value = totalWeight;
  row.getCell(weightColumn).numFmt = "#,##0.00";

  row.getCell(amountColumn).value = totalAmount;
  row.getCell(amountColumn).numFmt = "#,##0.00";

  row.commit();

  const branchName = branch?.name || shop?.name || "";
  const branchAddress = getAddressText(branch, shop);
  const branchImageUrl = getImageUrl(branch, shop);

  worksheet.getCell("D4").value =
    selectedData.customer?.name || selectedData.customerName || "";
  worksheet.getCell("I3").value = `${moment().format("DD/MM/YYYY")}`;
  worksheet.getCell("I1").value = selectedData.billNumber;
  worksheet.getCell("B2").value = branchName;
  worksheet.getCell("B3").value = branchAddress;
  worksheet.getCell("B2").alignment = { wrapText: true, vertical: "middle" };
  worksheet.getCell("B3").alignment = { wrapText: true, vertical: "middle" };

  await addBranchImage(workbook, worksheet, branchImageUrl);

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), "ReceivingForm.xlsx");
}
